/**
 * GalleryTemplateRenderer — the app-side glue for a gallery design.
 *
 * GAL-10. Runs the shared engine, loads the design's Google Fonts,
 * lazy-loads the design component, and renders the two overlays that
 * need app components (the Mux video player and the "Who's picking?"
 * dialog) so the designs themselves stay React-only.
 *
 * A gallery whose `template` has no built component yet falls back
 * to the caller-supplied `fallback` (today's ClassicGalleryBody), so
 * a client link can never render blank.
 */
import { Suspense, lazy, useEffect, useMemo, type ComponentType } from 'react';
import { Download, X } from 'lucide-react';
import { OrbitIcon } from '@/components/icons/OrbitIcon';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import BrandedVideoPlayer from '@/components/portal/BrandedVideoPlayer';
import type { PublicGalleryPayload } from '@/services/galleries';
import { useGalleryEngine, type GalleryEngine } from './useGalleryEngine';
import { DEFAULT_TEMPLATE_ID, googleFontsHref } from './themes';
import { useGoogleFonts } from './useGoogleFonts';
import type { GalleryTemplateId } from './types';

type TemplateComponent = ComponentType<{ engine: GalleryEngine }>;

/**
 * Built designs. Add an entry as each design lands; the editor's
 * picker reads `BUILT_TEMPLATES` so studios can only choose designs
 * that actually render.
 */
// GALLERY-ONE — one entry. `getLazyTemplate` already falls back to
// DEFAULT_TEMPLATE_ID for anything it doesn't know, so a gallery row
// still carrying a dead slug ('editorial', 'premiere', …) renders as
// Basic rather than erroring. That fallback is what lets the migration
// be a tidy-up rather than a prerequisite.
const TEMPLATE_COMPONENTS: Partial<Record<GalleryTemplateId, () => Promise<{ default: TemplateComponent }>>> = {
  basic: () => import('./photo/BasicTemplate'),
};

export const BUILT_TEMPLATES = new Set<GalleryTemplateId>(
  Object.keys(TEMPLATE_COMPONENTS) as GalleryTemplateId[],
);

export const isTemplateBuilt = (id: string | null | undefined): id is GalleryTemplateId =>
  !!id && BUILT_TEMPLATES.has(id as GalleryTemplateId);

const lazyCache = new Map<GalleryTemplateId, ReturnType<typeof lazy<TemplateComponent>>>();
/** The lazy component for a design — shared with the live design thumbnails. */
export const getLazyTemplate = (id: GalleryTemplateId) => {
  let c = lazyCache.get(id);
  if (!c) {
    const loader = TEMPLATE_COMPONENTS[id];
    if (!loader) return null;
    c = lazy(loader);
    lazyCache.set(id, c);
  }
  return c;
};

function formatVideoDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h > 0
    ? `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
    : `${m}:${s.toString().padStart(2, '0')}`;
}

export function GalleryTemplateRenderer({
  payload,
  demoBadge = false,
}: {
  payload: PublicGalleryPayload;
  demoBadge?: boolean;
}) {
  const engine = useGalleryEngine(payload, { demoBadge });
  // GAL-13 — every design is built and 'classic' is retired, so an
  // unknown id (a row written before this build) renders a design
  // rather than nothing — GALLERY-2: Basic, the default.
  const Template = useMemo(
    () => getLazyTemplate(engine.templateId) ?? getLazyTemplate(DEFAULT_TEMPLATE_ID),
    [engine.templateId],
  );

  useGoogleFonts(googleFontsHref(engine.theme));

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const prev = document.title;
    document.title = `${engine.gallery.name} · ${engine.studioName}`;
    return () => {
      document.title = prev;
    };
  }, [engine.gallery.name, engine.studioName]);

  if (!Template) return null;

  const { playingVideo, accent } = engine;

  return (
    <>
      <Suspense
        fallback={
          <div style={{ minHeight: '100vh', background: engine.theme.bg }} aria-busy="true" />
        }
      >
        <Template engine={engine} />
      </Suspense>

      {/* Film playback — Mux rendition through BrandedVideoPlayer
          (same plumbing as the classic body). */}
      {playingVideo && (
        <div
          className="fixed inset-0 z-[9985] flex items-center justify-center bg-black/95 p-4 animate-in fade-in duration-200"
          role="dialog"
          aria-modal="true"
          aria-label={playingVideo.file_name}
          onClick={engine.stopVideo}
        >
          <button
            type="button"
            className="absolute right-3 top-3 z-10 flex h-11 w-11 items-center justify-center rounded-full text-white/80 transition-colors hover:bg-white/10 hover:text-white sm:right-5 sm:top-5"
            onClick={engine.stopVideo}
            aria-label="Close video"
          >
            <OrbitIcon name="xmark" fallback={X} className="h-6 w-6" />
          </button>
          <div className="w-full max-w-4xl overflow-hidden rounded-xl bg-black" onClick={(e) => e.stopPropagation()}>
            <BrandedVideoPlayer
              src={playingVideo.mux_mp4_url ?? `https://stream.mux.com/${playingVideo.mux_playback_id}/capped-1080p.mp4`}
              poster={`https://image.mux.com/${playingVideo.mux_playback_id}/thumbnail.jpg?time=1`}
              brandColor={accent}
            />
            <div className="flex items-center justify-between gap-3 px-4 py-3">
              <p className="truncate text-sm font-medium text-white/85">{playingVideo.file_name}</p>
              <div className="flex shrink-0 items-center gap-3">
                {playingVideo.duration_seconds != null && (
                  <span className="text-xs tabular-nums text-white/50">
                    {formatVideoDuration(Number(playingVideo.duration_seconds))}
                  </span>
                )}
                {engine.downloadEnabled && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="gap-1.5 text-white/70 hover:text-white"
                    onClick={() => {
                      const a = document.createElement('a');
                      a.href = playingVideo.mux_mp4_url ?? `https://stream.mux.com/${playingVideo.mux_playback_id}/capped-1080p.mp4`;
                      a.download = playingVideo.file_name;
                      a.click();
                    }}
                  >
                    <OrbitIcon name="square.and.arrow.down" fallback={Download} className="h-3.5 w-3.5" />
                    Download
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* "Who's picking?" — one-time per browser, shared by every design. */}
      <Dialog open={engine.namePromptOpen} onOpenChange={(open) => { if (!open) engine.cancelName(); }}>
        <DialogContent className="max-w-sm bg-white text-stone-900 orbit-glass-none">
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <h3 className="text-base font-semibold">Who's picking?</h3>
              <p className="text-sm text-stone-500">Add your name so the studio knows whose favorites these are.</p>
            </div>
            <Input
              autoFocus
              placeholder="Your name (e.g. Sarah)"
              value={engine.pendingName}
              onChange={(e) => engine.setPendingName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') engine.submitName(); }}
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={engine.cancelName}>Cancel</Button>
              <Button onClick={engine.submitName} disabled={engine.pendingName.trim().length === 0} className="border-0 text-white" style={{ backgroundColor: accent }}>
                Save & pick
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default GalleryTemplateRenderer;
